(() => {
  'use strict';

  if (window.RavinInstant && window.RavinInstant.version) return;

  const root = document.documentElement;
  const enabled = root.dataset.rvInstantEnabled !== 'false';
  const showLine = root.dataset.rvInstantAcid !== 'false';
  const showPulse = root.dataset.rvInstantPulse !== 'false';
  const delay = Math.min(300, Math.max(100, Number(root.dataset.rvInstantDelay) || 150));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let line = null;
  let showTimer = 0;
  let resetTimer = 0;
  let pendingKey = '';
  let pendingSince = 0;

  function reset() {
    window.clearTimeout(showTimer);
    window.clearTimeout(resetTimer);
    showTimer = 0;
    resetTimer = 0;
    pendingKey = '';
    pendingSince = 0;
    if (line) line.classList.remove('is-primed', 'is-active');
  }

  function ensureLine() {
    if (line || !document.body) return line;
    line = document.createElement('div');
    line.className = 'rv-instant-progress';
    line.setAttribute('aria-hidden', 'true');
    const beam = document.createElement('span');
    beam.className = 'rv-instant-progress__beam';
    line.appendChild(beam);
    document.body.appendChild(line);
    return line;
  }

  function navigate(key) {
    if (!showLine || !key) return;
    if (pendingKey === key) return;
    reset();
    pendingKey = key;
    pendingSince = performance.now();
    const indicator = ensureLine();
    if (indicator) indicator.classList.add('is-primed');
    showTimer = window.setTimeout(() => {
      if (pendingKey && indicator) indicator.classList.add('is-active');
    }, delay);
    resetTimer = window.setTimeout(reset, 10000);
  }

  function isModified(event) {
    return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  }

  function validLink(anchor) {
    if (!anchor || anchor.hasAttribute('download') || anchor.matches('[aria-disabled="true"]') ||
        anchor.closest('[data-rv-instant-ignore]')) return null;
    const target = anchor.getAttribute('target');
    if (target && target.toLowerCase() !== '_self') return null;
    const raw = anchor.getAttribute('href');
    if (!raw || raw[0] === '#' || /^(?:javascript|mailto|tel|sms|data):/i.test(raw)) return null;
    let url;
    try { url = new URL(anchor.href, window.location.href); }
    catch (_) { return null; }
    if (!/^https?:$/.test(url.protocol) || url.origin !== window.location.origin) return null;
    if (url.pathname === window.location.pathname && url.search === window.location.search) return null;
    return url.href;
  }

  function actionable(target) {
    if (!(target instanceof Element)) return null;
    const node = target.closest('a[href], button, summary, [role="button"], input[type="submit"]');
    if (!node || node.matches('[disabled], [aria-disabled="true"]') ||
        node.closest('[data-rv-instant-ignore]')) return null;
    return node;
  }

  function pulseAt(event) {
    if (!showPulse || reduceMotion.matches || !document.body || !event.isPrimary) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!actionable(event.target)) return;

    const pulse = document.createElement('span');
    pulse.className = 'rv-instant-pulse';
    pulse.style.left = event.clientX + 'px';
    pulse.style.top = event.clientY + 'px';
    pulse.setAttribute('aria-hidden', 'true');
    document.body.appendChild(pulse);
    pulse.addEventListener('animationend', () => pulse.remove(), { once: true });
    window.setTimeout(() => pulse.remove(), 600);
  }

  function handleClick(event) {
    if (isModified(event) || !(event.target instanceof Element)) return;
    const anchor = event.target.closest('a[href]');
    if (!anchor) return;

    const localeOption = anchor.closest('localization-form') && anchor.hasAttribute('data-value');
    const filterLink = anchor.matches('a[data-filter-link], a[data-clear-all], a[data-clear-price]');
    const destination = validLink(anchor);
    const key = localeOption ? 'locale:' + anchor.dataset.value : destination;
    if (!key) return;

    if (pendingKey === key && performance.now() - pendingSince < 1200) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    queueMicrotask(() => {
      if (event.defaultPrevented && !localeOption && !filterLink) return;
      navigate(key);
    });
  }

  function handleSubmit(event) {
    if (!(event.target instanceof HTMLFormElement)) return;
    const form = event.target;
    if (form.method.toLowerCase() !== 'get' || !form.checkValidity()) return;
    if (form.closest('predictive-search')) return;
    const target = form.getAttribute('target');
    if (target && target.toLowerCase() !== '_self') return;

    queueMicrotask(() => {
      if (event.defaultPrevented) return;
      try {
        const url = new URL(form.action, window.location.href);
        if (url.origin === window.location.origin) navigate('form:' + url.pathname);
      } catch (_) {}
    });
  }

  function handleSort(event) {
    if (!(event.target instanceof Element)) return;
    const sort = event.target.closest('ravin-filters [data-sort-select]');
    if (sort) navigate('sort:' + sort.value);
  }

  function boot() {
    if (!enabled || !document.body) return;
    if (showLine) ensureLine();
    if (showPulse) document.addEventListener('pointerdown', pulseAt, { passive: true });
    if (showLine) {
      document.addEventListener('click', handleClick, true);
      document.addEventListener('submit', handleSubmit, true);
      document.addEventListener('change', handleSort, true);
      window.addEventListener('pagehide', reset);
      window.addEventListener('pageshow', reset);
    }
  }

  window.RavinInstant = { version: '1.1.0', reset, settings: { enabled, showLine, showPulse, delay } };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();